import type { ListInfoOutput } from "@orsane/schema/task-lists";
import { createRoute, notFound } from "@tanstack/react-router";
import { TRPCClientError } from "@trpc/client";
import { DiamondMinus } from "lucide-react";
import { Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useDocumentTitle } from "usehooks-ts";
import { z } from "zod";
import ContentFrame from "@/components/ContentFrame";
import SidePanel from "@/components/SidePanel";
import TaskListHeader from "@/components/TaskListHeader";
import TaskListNotFoundCallback from "@/components/TaskListNotFoundCallback";
import TaskListRouteSkeleton from "@/components/TaskListRouteSkeleton";
import { TaskQueryParamsProvider } from "@/components/task-table/TaskQueryParamsContext";
import TasksTable from "@/components/task-table/TasksTable";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";
import { useRouteTaskQueryParams } from "@/hooks/task-query-params/useRouteTaskQueryParams";
import { trpcClient } from "@/lib/trpc";
import { useSuspenseTaskList } from "@/queries/list-queries";
import { appRoute } from "./appRoute";

const taskListRouteParamsSchema = z.object({
    taskListId: z.coerce.number(),
});

const taskListSearchSchema = z.object({
    task: z.coerce.number().optional(),
    filter: z.any().optional(),
    tab: z.string().optional(),
});

export type TaskListRouteParams = z.infer<typeof taskListRouteParamsSchema>;

function Inner() {
    const { taskListId } = taskListRoute.useParams();
    const { data: taskList } = useSuspenseTaskList(taskListId);
    useDocumentTitle(`${taskList.name} - Orsane`);
    const queryParams = useRouteTaskQueryParams({ taskListId, type: "tasklist" });

    return (
        <TaskQueryParamsProvider value={queryParams}>
            <TaskListHeader taskList={taskList} />
            <TasksTable disabled={taskList.isArchived} />

            {/* Right Panel */}
            <SidePanel />
        </TaskQueryParamsProvider>
    );
}

export const taskListRoute = createRoute({
    getParentRoute: () => appRoute,
    path: "/lists/$taskListId",
    params: taskListRouteParamsSchema,
    validateSearch: taskListSearchSchema,
    notFoundComponent: ({ data }: { data: any }) => {
        return (
            <ContentFrame>
                <Empty className="h-full">
                    <EmptyHeader>
                        <EmptyMedia variant="icon" className="bg-red-100/60 text-red-600">
                            <DiamondMinus />
                        </EmptyMedia>
                        <EmptyTitle>List not found</EmptyTitle>
                        <EmptyDescription>
                            The task list you are looking for does not exist, has been deleted or you are not allowed to
                            view it.
                        </EmptyDescription>
                    </EmptyHeader>
                </Empty>
            </ContentFrame>
        );
    },
    loader: async ({ params }) => {
        let info: ListInfoOutput;
        try {
            info = await trpcClient.taskList.getInfoById.query({ id: params.taskListId });

            return info;
        } catch (e) {
            if (e instanceof TRPCClientError && e.data.code === "NOT_FOUND") {
                throw notFound({
                    data: {
                        code: "NOT_FOUND",
                        message: "List not found",
                    },
                });
            }
            throw e;
        }
    },
    component: () => {
        return (
            <ContentFrame>
                <ErrorBoundary FallbackComponent={TaskListNotFoundCallback}>
                    <Suspense fallback={<TaskListRouteSkeleton />}>
                        <Inner />
                    </Suspense>
                </ErrorBoundary>
            </ContentFrame>
        );
    },
});
